import { get } from 'lodash';
import moment from 'moment';
import { formMixin } from '@/components/forms/mixin';
import plusMinusSign from '@/components/generics/plus-minus-sign/plus-minus-sign.vue';
import env from '@/env';
import { withMeta } from '@/helpers/dataLayer';
import appNotificationMixin from '@/mixins/appNotification';
import { isWrapperApp } from '@/mixins/userAgent';
import { isOfferMatched } from '@/store/modules/notifications';
import { base32 } from '@/utils/format';
import { isMarketSingle, toCents, getAssetPath } from '@/utils/helpers';

const DATE_FORMAT = 'MM/DD/YYYY hh:mm a';

export default {
  mixins: [appNotificationMixin, formMixin],
  components: {
    plusMinusSign,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dontShowAgain: false,
      advanced: false,
    };
  },
  computed: {
    isWrapperApp() {
      return isWrapperApp;
    },
    classModifiers() {
      return {
        'notification-item-trade-confirmed': true,
        'notification-item-trade-confirmed--processing': this.isProcessedOrMatched,
      };
    },
    showFooter() {
      return !env.RESTRICTED_ACCESS_ENABLED;
    },
    minPrice() {
      return toCents(this.data.Offer.PricePerShare);
    },
    minPriceDisplay() {
      const suffix = this.minPrice === 1 ? 'cent' : 'cents';
      return `${this.minPrice} ${suffix}`;
    },
    profit() {
      return this.data.TotalProfit;
    },
    fee() {
      return this.data.TotalFees;
    },
    cost() {
      return this.data.TotalCost;
    },
    risk() {
      return this.data.TotalRiskChange;
    },
    showRisk() {
      return this.data.ShowRisk;
    },
    contractName() {
      return this.data.Name || this.data.LongName;
    },
    marketName() {
      return this.data.MarketName;
    },
    isMarketSingle() {
      return isMarketSingle(this.data);
    },
    history() {
      const source = this.data.History ? this.data.History.map((i) => ({ ...i })) : [];
      return (
        source.map((h) => {
          h.PricePerShare = toCents(h.PricePerShare);
          return h;
        }) || []
      );
    },
    dateCreated() {
      const v = get(this.data, 'Offer.DateCreated', null);
      return v !== null ? `${moment(v).format(DATE_FORMAT)} ET` : null;
    },
    defaultTradeType() {
      return parseInt(this.data.Offer.TradeType, 10) || 0;
    },
    offerType() {
      return this.data.Offer.TradeType;
    },
    offerId() {
      return this.data.Offer.OfferId;
    },
    remainingQuantity() {
      return this.data.Offer.RemainingQuantity;
    },
    isOfferProcessed() {
      return (
        this.data.History !== null &&
        this.data.History.length > 0 &&
        this.data.Offer.RemainingQuantity !== 0
      );
    },
    isOfferMatched() {
      return isOfferMatched(this.data);
    },
    isOpenOfferRecorded() {
      return this.data.Offer.RemainingQuantity !== 0;
    },
    isProcessedOrMatched() {
      return this.isOfferProcessed || this.isOfferMatched;
    },
    offerStatusLongText() {
      if (this.isOfferProcessed) return 'Order Matched!';
      if (this.isOfferMatched) return 'Order Matched!';
      if (this.isOpenOfferRecorded) return 'Order Recorded';
      return null;
    },
    showHistory() {
      return this.isProcessedOrMatched && this.history && this.history.length > 0;
    },
    showCashFooter() {
      return this.isProcessedOrMatched && this.advanced;
    },
    showOpenOfferRecordedFooter() {
      return this.isOpenOfferRecorded && this.advanced;
    },
    showToggle() {
      return true;
      // return this.isProcessedOrMatched;
    },
    showProfitLoss() {
      return this.defaultTradeType > 1;
    },
    showFees() {
      return this.defaultTradeType > 1 && this.fee !== 0;
    },
    shareText() {
      if (this.history.length > 0) {
        if (this.defaultTradeType <= 1) {
          // show max price when user buy shares
          const h = this.history.reduce((prev, current) =>
            prev.PricePerShare > current.PricePerShare ? prev : current,
          );
          return this.getShareText('buy', h.Quantity, h.PricePerShare);
        }
        // show min price when user sell shares
        const h = this.history.reduce((prev, current) =>
          prev.PricePerShare < current.PricePerShare ? prev : current,
        );
        return this.getShareText('sell', h.Quantity, h.PricePerShare);
      }
      if (this.defaultTradeType <= 1) {
        return this.getShareText('buy-offer', this.remainingQuantity, this.minPrice);
      }
      return this.getShareText('sell-offer', this.remainingQuantity, this.minPrice);
    },
    shareUrl() {
      const resolved = this.$router.resolve({
        name: 'market-details',
        params: {
          marketId: this.data.MarketId,
          marketUrl: this.data.MarketUrl,
        },
      });
      return `${window.location.origin}${resolved.href}`;
    },
    facebookShareUrl() {
      const pictureUrl = getAssetPath(`/images/Contracts/${this.data.ImageName}`);
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.shareUrl)}&picture=${encodeURIComponent(pictureUrl)}&quote=${encodeURIComponent(this.shareText)}`;
    },
    twitterShareUrl() {
      return `http://twitter.com/share?text=${encodeURIComponent(this.shareText)}&url=${encodeURIComponent(this.shareUrl)}`;
    },
    toggleButtonModifiers() {
      return {
        'notification-item-trade-confirmed__toggle': true,
        'notification-item-trade-confirmed__toggle--on': this.advanced,
      };
    },
    profitOrLossText() {
      return this.data.TotalProfit > 0 ? 'Profit' : 'Loss';
    },
    bottomContainerClassModifiers() {
      return {
        'notification-item-trade-confirmed__container': true,
        'notification-item-trade-confirmed__container--simple': !this.isProcessedOrMatched,
        'notification-item-trade-confirmed__container--border': this.isProcessedOrMatched,
      };
    },
    mailto() {
      const text = this.shareText;
      const newShareText = text.replace(' @PredictIt', '');
      return `mailto:?body=${newShareText}${escape('\r\n')}${escape('\r\n')}${escape('\r\n')}${this.shareUrl}&subject=I just traded on PredictIt.org!`;
    },
  },
  methods: {
    base32,
    soldOrBoughtTextOffer() {
      return this.defaultTradeType <= 1 ? 'Buy Order' : 'Sell Order';
    },
    soldOrBoughtText(tradeType = null) {
      const isHistoryPresent = !!this.data.History;
      if (tradeType === null) return this.soldOrBoughtText(this.defaultTradeType);
      if (tradeType <= 1) return isHistoryPresent ? 'Bought' : 'Buy';
      return isHistoryPresent ? 'Sold' : 'Sell';
    },
    isYes(tradeType) {
      return tradeType === 1 || tradeType === 3;
    },
    yesNoText(tradeType) {
      return this.isYes(tradeType) ? 'YES' : 'NO';
    },
    tradeTypeClassModifier(tradeType) {
      return {
        'notification-item-trade-confirmed__num-shares': true,
        'notification-item-trade-confirmed__num-shares--yes': this.isYes(tradeType),
        'notification-item-trade-confirmed__num-shares--no': !this.isYes(tradeType),
      };
    },
    onToggle() {
      this.advanced = !this.advanced;
    },
    openShareDialog(url) {
      window.open(
        url,
        '',
        'menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=600,width=600',
      );
    },
    onEmailShare() {
      this.$gtm.trackEvent(withMeta({ event: 'Share-Email' }));
    },
    onFacebookShare() {
      this.$gtm.trackEvent(withMeta({ event: 'Share-Facebook' }));
      this.openShareDialog(this.facebookShareUrl);
    },
    onTwitterShare() {
      this.$gtm.trackEvent(withMeta({ event: 'Share-Twitter' }));
      this.openShareDialog(this.twitterShareUrl);
    },
    getShareText(type, quantity, pricePerShare) {
      let soldOrBoughtShareText = 'I just bought';
      switch (type) {
        default:
        case 'buy':
          break;
        case 'sell':
          soldOrBoughtShareText = 'I just sold';
          break;
        case 'buy-offer':
          soldOrBoughtShareText = "I'm placing an order to buy";
          break;
        case 'sell-offer':
          soldOrBoughtShareText = "I'm placing an order to sell";
          break;
      }

      const suffix = quantity > 1 ? 'shares' : 'share';
      const tradeTypeText =
        this.defaultTradeType === 0 || this.defaultTradeType === 2 ? 'No' : 'Yes';
      const contractSuffix = this.isMarketSingle ? '' : ` in '${this.contractName}'`;
      const shares = `${quantity} ${tradeTypeText} ${suffix}${contractSuffix}`;

      return `${soldOrBoughtShareText} ${shares} for ${pricePerShare}¢ in this market. @PredictIt`;
    },
  },
};
